import React, { useCallback, useEffect, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import { useIsDarkMode } from 'public/components/default_template/meta/useTheme'
import cx from 'classnames'
import { AuthenticationSource } from '../../checkout/checkoutUtils'
import { PwlessAuth } from '../pwlessAuth/PwlessAuth'
import { Modal, ModalOverlay, useModal } from 'shared/components/common/modal'
import { useCustomer } from 'src/public/components/online_ordering/CustomerContextCommon'
import { t } from 'src/translations/src'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { useGlobalNav } from 'src/public/components/default_template/GlobalNavContext'
import { isToastLocalRequest } from 'src/public/js/siteUtilities'
import { useStaticSiteContext } from 'src/public/components/app/StaticSiteContext'
import {
  OO_ANCHOR,
  TOAST_LOCAL_ANCHOR,
  AUTH_TOOLTIP_TIMEOUT,
  getAuthTooltipCookieKey,
  setAuthTooltipCookie,
  TOAST_LOCAL_IDENTIFIER
} from './authTooltipUtil'
import { useCookies } from 'react-cookie'
import { useOptionalRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'

type AuthTooltipProps = {
  globalNavOpen?: boolean
  isDarkMode?: boolean
}

const ContextualAuthTooltip = () => {
  const { globalNavOpen } = useGlobalNav()
  const isDarkMode = useIsDarkMode()

  return (
    <AuthTooltipBase globalNavOpen={globalNavOpen} isDarkMode={isDarkMode} />
  )
}

const AuthTooltipBase = ({
  globalNavOpen = true,
  isDarkMode
}: AuthTooltipProps) => {
  const { isOpen, onClose, onOpen: onOpenModal } = useModal()
  const { customer } = useCustomer()
  const { track } = useTracker()
  const [isAuthTooltipOpen, setIsAuthTooltipOpen] = useState(true)
  const rxContext = useOptionalRestaurant()
  const rxGuid = rxContext?.ooRestaurant?.guid ?? TOAST_LOCAL_IDENTIFIER

  const authTooltipCookieKey = getAuthTooltipCookieKey(rxGuid)
  const [cookies, setCookie] = useCookies([authTooltipCookieKey])

  const tooltipHasBeenClosed = cookies[authTooltipCookieKey] === 'true'

  const closeAuthTooltip = useCallback(() => {
    setIsAuthTooltipOpen(false)
    setAuthTooltipCookie(rxGuid, setCookie)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rxGuid])

  const anchorSelect =
    typeof document !== 'undefined' &&
    typeof window !== 'undefined' &&
    document.querySelector(OO_ANCHOR)
      ? OO_ANCHOR
      : TOAST_LOCAL_ANCHOR

  useEffect(() => {
    if (!globalNavOpen || tooltipHasBeenClosed) {
      closeAuthTooltip()
    }
  }, [closeAuthTooltip, globalNavOpen, tooltipHasBeenClosed])

  const handleClick = () => {
    closeAuthTooltip()
    onOpenModal()
    track(TrackingEvents.STARTED_AUTHENTICATION, {
      source: AuthenticationSource.UserNav,
      isAuthTooltipVisible: true
    })
  }
  useEffect(() => {
    if (!isAuthTooltipOpen) {
      // ensure cookie is set when tooltip is closed
      closeAuthTooltip()
      return
    }
    const timer = setTimeout(() => {
      closeAuthTooltip()
    }, AUTH_TOOLTIP_TIMEOUT)
    return () => clearTimeout(timer)
  }, [isAuthTooltipOpen, closeAuthTooltip])

  if (customer) {
    return null
  }

  return (
    <>
      {isAuthTooltipOpen && (
        <Tooltip
          id='auth-tooltip'
          anchorSelect={anchorSelect}
          isOpen={isAuthTooltipOpen}
          clickable
          setIsOpen={setIsAuthTooltipOpen}
          closeEvents={{
            click: true,
            mouseleave: false,
            mouseout: false,
            mouseup: false,
            blur: false
          }}
          openEvents={{ mouseenter: false, mouseover: false, focus: false }}
          variant={isDarkMode ? 'light' : 'dark'}
          place={'bottom-end'}
          className={cx('authTooltip', isDarkMode ? 'light' : 'dark')}
          offset={8}
          classNameArrow='authTooltipArrow'
        >
          <button className='authTooltipContent' onClick={handleClick}>
            <span>{t('auth-tooltip.sign-in')}</span>
            {t('auth-tooltip.for-a-faster-ordering-experience')}
          </button>
        </Tooltip>
      )}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay fadeIn fadeOut />
        <PwlessAuth source={AuthenticationSource.UserNav} />
      </Modal>
    </>
  )
}

const AuthTooltip = () => {
  const staticContext = useStaticSiteContext()
  const isToastLocal = isToastLocalRequest(staticContext)
  return isToastLocal ? <AuthTooltipBase /> : <ContextualAuthTooltip />
}

export default AuthTooltip
